import { FireInsight, FireBook } from '../models/FirestoreModels';
import { BookRepository } from '../repositories/bookRepository';
import { PrivateProfileService } from './privateProfileService';
import { BookService } from './bookService';
import { auth } from '../config/firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Service response type matching our pattern
 */
export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Insights feed page data
 */
export interface InsightsFeedPage {
  items: Array<{
    insight: FireInsight;
    book?: FireBook; // Optional book context
  }>;
  nextCursor?: string;
  hasMore: boolean;
}

/**
 * Feed source type
 */
type FeedSource = 'recommended' | 'popular';

/**
 * Internal cursor structure for pagination
 * Encodes position in the feed: source, book index, insight index
 */
interface FeedCursor {
  source: FeedSource;
  bookIndex: number;
  insightIndex: number;
  seenInsightIds: string[]; // Track seen insights to avoid duplicates
}

/**
 * Encode cursor to base64 string (React Native compatible)
 */
function encodeCursor(cursor: FeedCursor): string {
  try {
    const jsonString = JSON.stringify(cursor);
    // Use btoa (available in React Native)
    return btoa(jsonString);
  } catch (error) {
    console.error('Error encoding cursor:', error);
    return '';
  }
}

/**
 * Decode cursor from base64 string (React Native compatible)
 */
function decodeCursor(cursorString: string): FeedCursor | null {
  try {
    // Use atob (available in React Native)
    const decoded = atob(cursorString);
    return JSON.parse(decoded) as FeedCursor;
  } catch (error) {
    console.error('Error decoding cursor:', error);
    return null;
  }
}

/**
 * Service for managing the insights feed
 * Provides paginated, personalized insights for the TikTok-style feed
 * 
 * Short-term: Pages through recommended books → popular books (client-side)
 * Future: Can be easily swapped to call backend getInsightsFeed function
 */
export class InsightsFeedService {
  private static readonly DEFAULT_PAGE_SIZE = 10;
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  private static readonly SEEN_INSIGHTS_KEY = 'insights_feed_seen_ids';
  private static readonly SEEN_INSIGHTS_EXPIRY_DAYS = 7; // Reset after 7 days

  // Cache for book lists (to avoid re-fetching on each page)
  private static bookListCache: {
    recommendedBookIds: string[];
    popularBookIds: string[];
    timestamp: number;
    userId: string | null;
  } = {
    recommendedBookIds: [],
    popularBookIds: [],
    timestamp: 0,
    userId: null,
  };

  // In-memory cache of seen insights
  private static seenInsightsCache: Set<string> | null = null;

  /**
   * Get a page of insights for the feed
   * 
   * @param pageSize Number of insights per page
   * @param cursor Pagination cursor (base64-encoded position)
   * @returns Page of insights with navigation info
   */
  static async getInsightsFeedPage(
    pageSize: number = this.DEFAULT_PAGE_SIZE,
    cursor?: string
  ): Promise<ServiceResponse<InsightsFeedPage>> {
    try {
      const userId = auth.currentUser?.uid;

      // Decode cursor or start fresh
      let feedCursor: FeedCursor;
      if (cursor) {
        const decoded = decodeCursor(cursor);
        if (!decoded) {
          return {
            success: false,
            error: 'Invalid cursor',
          };
        }
        feedCursor = decoded;
      } else {
        // Start from beginning
        feedCursor = {
          source: 'recommended',
          bookIndex: 0,
          insightIndex: 0,
          seenInsightIds: [],
        };
      }

      // Ensure book lists are cached
      await this.ensureBookListsCache(userId);

      // Fetch insights for this page
      const result = await this.fetchPagedInsights(
        feedCursor,
        pageSize,
        userId
      );

      if (result.insights.length === 0) {
        return {
          success: false,
          error: 'No more insights available',
        };
      }

      // Get unique book IDs that aren't already cached
      const uniqueBookIds = [...new Set(result.insights.map(i => i.bookId))];
      const uncachedBookIds = uniqueBookIds.filter(id => !this.bookDataCache.has(id));

      // Batch fetch any missing books (uses cache)
      if (uncachedBookIds.length > 0) {
        const booksResult = await BookService.getBooksById(uncachedBookIds, 'en');
        if (booksResult.success && booksResult.data) {
          booksResult.data.forEach(book => {
            this.bookDataCache.set(book.id, book);
          });
        }
      }

      // Use cached book data (already fetched in parallel)
      const insightsWithBooks = result.insights.map(insight => ({
        insight,
        book: this.bookDataCache.get(insight.bookId),
      }));

      const page: InsightsFeedPage = {
        items: insightsWithBooks,
        hasMore: result.hasMore,
        nextCursor: result.nextCursor,
      };

      return {
        success: true,
        data: page,
      };
    } catch (error) {
      console.error('Error fetching insights feed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch insights feed',
      };
    }
  }

  /**
   * Ensure book lists are cached for pagination
   */
  private static async ensureBookListsCache(userId: string | undefined): Promise<void> {
    const now = Date.now();
    
    // Check if cache is valid
    if (
      this.bookListCache.userId === (userId || null) &&
      now - this.bookListCache.timestamp < this.CACHE_TTL_MS &&
      (this.bookListCache.recommendedBookIds.length > 0 || this.bookListCache.popularBookIds.length > 0)
    ) {
      return; // Cache is valid
    }

    // Fetch fresh book lists
    console.log('📚 Refreshing book lists cache...');

    let recommendedBookIds: string[] = [];
    let popularBookIds: string[] = [];

    // Fetch recommended books if user is logged in
    if (userId) {
      try {
        const recommendationsResult = await PrivateProfileService.getCurrentRecommendations(userId);
        if (recommendationsResult.success && recommendationsResult.data) {
          recommendedBookIds = recommendationsResult.data.recommendedBooks?.map(rec => {
            const bookId = rec.bookId;
            return bookId.includes('_') ? bookId.split('_')[0] : bookId;
          }) || [];
          console.log(`✅ Found ${recommendedBookIds.length} recommended books`);
        }
      } catch (error) {
        console.warn('Error fetching recommended books:', error);
      }
    }

    // Always fetch popular books as fallback
    try {
      const popularResult = await BookService.getPopularBooks('en', 20);
      if (popularResult.success && popularResult.data) {
        popularBookIds = popularResult.data.map(book => {
          const bookId = book.id;
          return bookId.includes('_') ? bookId.split('_')[0] : bookId;
        });
        console.log(`✅ Found ${popularBookIds.length} popular books`);
      }
    } catch (error) {
      console.warn('Error fetching popular books:', error);
    }

    // Update cache
    this.bookListCache = {
      recommendedBookIds,
      popularBookIds,
      timestamp: now,
      userId: userId || null,
    };
  }

  /**
   * Fetch a page of insights based on cursor position
   * Optimized to batch-fetch multiple books in parallel
   */
  private static async fetchPagedInsights(
    cursor: FeedCursor,
    pageSize: number,
    userId: string | undefined
  ): Promise<{
    insights: FireInsight[];
    hasMore: boolean;
    nextCursor?: string;
  }> {
    const seenIds = new Set(cursor.seenInsightIds);
    
    // Load previously viewed insights to filter them out
    const persistentlySeenIds = await this.loadSeenInsights();
    if (persistentlySeenIds.size > 0) {
      console.log(`🔍 Filtering out ${persistentlySeenIds.size} previously seen insights`);
    }
    
    const insights: FireInsight[] = [];
    let currentCursor = { ...cursor };
    let totalFiltered = 0;
    const BATCH_SIZE = 5; // Fetch up to 5 books in parallel at a time
    const MAX_BOOKS_TO_CHECK = 50; // Safety limit

    // Try to collect pageSize insights (filtering out previously seen ones)
    let booksChecked = 0;
    while (insights.length < pageSize && booksChecked < MAX_BOOKS_TO_CHECK) {
      // Determine which book list to use
      const bookIds = currentCursor.source === 'recommended' 
        ? this.bookListCache.recommendedBookIds
        : this.bookListCache.popularBookIds;

      // Check if we've exhausted this source
      if (currentCursor.bookIndex >= bookIds.length) {
        if (currentCursor.source === 'recommended' && this.bookListCache.popularBookIds.length > 0) {
          // Switch to popular books
          console.log('📚 Switching from recommended to popular books');
          currentCursor.source = 'popular';
          currentCursor.bookIndex = 0;
          currentCursor.insightIndex = 0;
          continue;
        } else {
          // No more books available
          break;
        }
      }

      // Batch fetch multiple books at once for better performance
      const batchBookIds = bookIds.slice(
        currentCursor.bookIndex,
        currentCursor.bookIndex + BATCH_SIZE
      );
      booksChecked += batchBookIds.length;

      // Fetch all books in parallel
      const batchResults = await Promise.all(
        batchBookIds.map(async (bookId) => {
          try {
            const bookInsights = await this.getBookInsights(bookId);
            return { bookId, insights: bookInsights, error: null };
          } catch (error) {
            console.warn(`Error fetching insights for book ${bookId}:`, error);
            return { bookId, insights: [], error };
          }
        })
      );

      // Process results from batch
      for (const { bookId, insights: bookInsights } of batchResults) {
        // Handle cursor position for first book in batch
        const isFirstBookInBatch = bookId === batchBookIds[0];
        const startIndex = isFirstBookInBatch ? currentCursor.insightIndex : 0;

        // Find insights we haven't seen yet from this book
        const availableInsights = bookInsights.slice(startIndex);
        
        const unseenInsights = availableInsights.filter(insight => {
          const inSessionSeen = seenIds.has(insight.id);
          const persistentlySeen = persistentlySeenIds.has(insight.id);
          
          if (inSessionSeen || persistentlySeen) {
            totalFiltered++;
          }
          
          return !inSessionSeen && !persistentlySeen;
        });

        // Add as many as we can to reach pageSize
        const toAdd = unseenInsights.slice(0, pageSize - insights.length);
        toAdd.forEach(insight => {
          insights.push(insight);
          seenIds.add(insight.id);
        });

        // If we got enough insights, stop processing this batch
        if (insights.length >= pageSize) {
          // Update cursor to reflect where we stopped
          currentCursor.insightIndex = startIndex + toAdd.length;
          break;
        } else {
          // Move to next book
          currentCursor.bookIndex++;
          currentCursor.insightIndex = 0;
        }
      }

      // If we got all we needed, break
      if (insights.length >= pageSize) {
        break;
      }
    }

    // Determine if there are more insights available
    const bookIds = currentCursor.source === 'recommended'
      ? this.bookListCache.recommendedBookIds
      : this.bookListCache.popularBookIds;
    
    const hasMoreInCurrentSource = currentCursor.bookIndex < bookIds.length;
    const canSwitchToPopular = 
      currentCursor.source === 'recommended' && 
      this.bookListCache.popularBookIds.length > 0;
    
    const hasMore = hasMoreInCurrentSource || canSwitchToPopular;

    // Encode next cursor
    const nextCursor = hasMore ? encodeCursor({
      ...currentCursor,
      seenInsightIds: Array.from(seenIds),
    }) : undefined;

    // Log only if interesting (filtered something or found nothing)
    if (totalFiltered > 0 || insights.length === 0) {
      console.log(`📊 Filtered ${totalFiltered} seen insights, returning ${insights.length} new`);
    }
    
    // If we couldn't find enough insights, return what we have
    if (insights.length === 0) {
      console.warn('⚠️ No unseen insights found! All available insights have been seen.');
    }

    return {
      insights,
      hasMore: hasMore && insights.length > 0, // Only hasMore if we actually found some
      nextCursor: insights.length > 0 ? nextCursor : undefined,
    };
  }

  /**
   * Get insights for a specific book (with caching)
   */
  private static bookInsightsCache = new Map<string, FireInsight[]>();
  private static bookDataCache = new Map<string, FireBook>();

  private static async getBookInsights(bookId: string): Promise<FireInsight[]> {
    // Check cache
    if (this.bookInsightsCache.has(bookId)) {
      return this.bookInsightsCache.get(bookId)!;
    }

    // Fetch book + insights using BookService (uses SQLite cache!)
    try {
      const result = await BookService.getBookDetail(bookId, 'en');
      if (!result.success || !result.data) {
        this.bookInsightsCache.set(bookId, []);
        return [];
      }

      const { book, insights } = result.data;

      // Cache book data for later use
      this.bookDataCache.set(bookId, book);

      // Shuffle insights for variety
      const shuffled = this.shuffleArray([...insights]);
      this.bookInsightsCache.set(bookId, shuffled);
      return shuffled;
    } catch (error) {
      console.error(`Error fetching insights for book ${bookId}:`, error);
      this.bookInsightsCache.set(bookId, []);
      return [];
    }
  }

  /**
   * Shuffle array (Fisher-Yates algorithm)
   */
  private static shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Load seen insights from persistent storage
   */
  private static async loadSeenInsights(): Promise<Set<string>> {
    if (this.seenInsightsCache) {
      return this.seenInsightsCache;
    }

    try {
      const stored = await AsyncStorage.getItem(this.SEEN_INSIGHTS_KEY);
      if (!stored) {
        this.seenInsightsCache = new Set();
        return this.seenInsightsCache;
      }

      const data = JSON.parse(stored);
      const { insights, timestamp } = data;

      // Check if expired (older than 7 days)
      const daysSince = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
      if (daysSince > this.SEEN_INSIGHTS_EXPIRY_DAYS) {
        console.log('🔄 Seen insights expired, resetting...');
        this.seenInsightsCache = new Set();
        await AsyncStorage.removeItem(this.SEEN_INSIGHTS_KEY);
        return this.seenInsightsCache;
      }

      this.seenInsightsCache = new Set(insights);
      return this.seenInsightsCache;
    } catch (error) {
      console.error('Error loading seen insights:', error);
      this.seenInsightsCache = new Set();
      return this.seenInsightsCache;
    }
  }

  /**
   * Save seen insights to persistent storage
   */
  private static async saveSeenInsights(seenIds: Set<string>): Promise<void> {
    try {
      const data = {
        insights: Array.from(seenIds),
        timestamp: Date.now(),
      };
      await AsyncStorage.setItem(this.SEEN_INSIGHTS_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving seen insights:', error);
    }
  }

  /**
   * Mark insights as seen (called when user views them)
   */
  static async markInsightsAsSeen(insightIds: string[]): Promise<void> {
    const seenInsights = await this.loadSeenInsights();
    let added = 0;

    insightIds.forEach(id => {
      if (!seenInsights.has(id)) {
        seenInsights.add(id);
        added++;
      }
    });

    if (added > 0) {
      await this.saveSeenInsights(seenInsights);
      // Silently save - only log if there's an issue
    }
  }

  /**
   * Clear seen insights (for testing or user request)
   */
  static async clearSeenInsights(): Promise<void> {
    this.seenInsightsCache = new Set();
    await AsyncStorage.removeItem(this.SEEN_INSIGHTS_KEY);
    console.log('🔄 Cleared all seen insights');
  }

  /**
   * Clear all caches (e.g., when user logs out or refreshes)
   */
  static clearCache(): void {
    this.bookListCache = {
      recommendedBookIds: [],
      popularBookIds: [],
      timestamp: 0,
      userId: null,
    };
    this.bookInsightsCache.clear();
    this.bookDataCache.clear();
    // Note: Don't clear seen insights on cache clear, only on explicit clearSeenInsights()
  }

  /**
   * Prefetch next page (for performance optimization)
   * Call this when user is approaching the end of current items
   */
  static async prefetchNextPage(cursor?: string): Promise<void> {
    if (!cursor) return;

    // Fire and forget - prefetch in background
    this.getInsightsFeedPage(this.DEFAULT_PAGE_SIZE, cursor).catch(error => {
      console.warn('Prefetch failed (non-critical):', error);
    });
  }
}

